import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useApolloClient, useMutation } from '@apollo/client'
import { LOGIN } from '../graphql/mutations'
import { auth, AvailableRole } from '../lib/auth'
import { navigation } from '../lib/navigation'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'
import RoleSelector from '../components/RoleSelector'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [roleSelectionData, setRoleSelectionData] = useState<{
    availableRoles: AvailableRole[]
    userEmail: string
  } | null>(null)
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const location = useLocation()

  // Check for intended destination on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const redirectTo = urlParams.get('redirect')
    if (redirectTo) {
      navigation.setIntendedDestination(redirectTo)
    }
  }, [location])

  const [login, { loading }] = useMutation(LOGIN, {
    onCompleted: async (data) => {
      try {
        const loginResponse = data.login

        // Check if role selection is required
        if (loginResponse.requiresRoleSelection && loginResponse.availableRoles) {
          // Show role selector
          setRoleSelectionData({
            availableRoles: loginResponse.availableRoles,
            userEmail: email,
          })
          return
        }

        // Login complete - store authentication data
        if (loginResponse.token && loginResponse.role) {
          // Clear role selection data if it was set
          setRoleSelectionData(null)

          auth.setAuth({
            user: loginResponse.user,
            role: loginResponse.role,
            token: loginResponse.token,
            refreshToken: loginResponse.refreshToken,
            expiresIn: loginResponse.expiresIn,
          })

          // Ensure we don't reuse cached data (especially permissions) from a previous user/session.
          // This also forces active queries to refetch using the new token.
          try {
            await apolloClient.resetStore()
          } catch (e) {
            // Non-fatal; navigation + auth still works.
          }

          // Show success message
          toast.success('Login successful! Welcome back!')

          // Get intended destination or default to dashboard
          const intendedDestination = navigation.getIntendedDestination()

          // Clear the intended destination
          navigation.clearIntendedDestination()

          // Force a storage event to trigger App component re-render
          window.dispatchEvent(new Event('storage'))

          // Redirect immediately to intended destination or dashboard
          setTimeout(() => {
            navigate(intendedDestination, { replace: true })
          }, 100)
        }
      } catch (error) {
        toast.error('Login successful but there was an issue setting up your session')
        navigate('/', { replace: true })
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Login failed. Please check your credentials.')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    login({
      variables: {
        input: { email, password }
      }
    })
  }

  const handleRoleSelected = async (roleId: number) => {
    try {
      // Call login again with selected role
      await login({
        variables: {
          input: {
            email,
            password,
            selectedRoleId: roleId,
          }
        }
      })

      // The onCompleted handler will handle the successful login
    } catch (error: any) {
      toast.error(error.message || 'Failed to login with selected role. Please try again.')
    }
  }

  const handleCancelRoleSelection = () => {
    setRoleSelectionData(null)
    setPassword('') // Clear password for security
  }

  // Show role selector if needed
  if (roleSelectionData) {
    return (
      <RoleSelector
        availableRoles={roleSelectionData.availableRoles}
        userEmail={roleSelectionData.userEmail}
        onRoleSelected={handleRoleSelected}
        onCancel={handleCancelRoleSelection}
      />
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-seasalt py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <img src="/brand/logo.svg" alt="Curry Me POS" className="h-14 w-auto" />
          </div>
          <h2 className="mt-6 text-center font-primary text-h4 font-bold text-secondary-900">
            Curry Me POS
          </h2>
          <p className="mt-2 text-center text-body text-secondary-600">
            Sign in to your account
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="rounded-t-lg"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="rounded-b-lg"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              isLoading={loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </Button>
          </div>

          <p className="text-center">
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Forgot password?
            </Link>
          </p>

        </form>
      </div>
    </div>
  )
}

export default LoginPage
